import * as React from 'react';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import { PRESENCE_COLORS, PresenceState, PresenceStatus } from '@ixicare/resources';
import { t } from '~i18n';
import { useStores } from '~store/utils';
import styles from './StatusBadge.module.css';

export interface StatusBadgeProps {
  status: PresenceStatus | null | undefined;
  /** Show only the dot, no label. */
  iconOnly?: boolean;
  className?: string;
}

const STATUS_LABEL_KEYS: Record<PresenceState, string> = {
  [PresenceState.Online]: 'presence.state.online',
  [PresenceState.Occupied]: 'presence.state.occupied',
  [PresenceState.Unavailable]: 'presence.state.unavailable',
  [PresenceState.Offline]: 'presence.state.offline',
};

/** Tolerate `Date | string` — backend serialization sometimes hands us a
 *  string even though the type says Date. */
export function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatRemaining(occupiedUntil: Date | string, now: number = Date.now()): string {
  const d = toDate(occupiedUntil);
  if (!d) return '';
  const diffMs = d.getTime() - now;
  if (diffMs <= 0) return t('presence.duration.minutes', { count: 0 });
  const totalMin = Math.ceil(diffMs / 60_000);
  if (totalMin < 60) return t('presence.duration.minutes', { count: totalMin });
  const hours = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  return min === 0
    ? t('presence.duration.hours', { count: hours })
    : t('presence.duration.hoursMinutes', { hours, minutes: min });
}

const StatusBadge: React.FC<StatusBadgeProps> = observer(function StatusBadge({ status, iconOnly, className }) {
  const { presence } = useStores();
  const state = status?.state ?? PresenceState.Offline;
  const color = PRESENCE_COLORS[state];
  const isOffline = state === PresenceState.Offline;
  const isCountingDown = state === PresenceState.Occupied && !!status?.occupiedUntil;
  // Subscribe to the store tick so MobX re-renders this badge in lockstep
  // with every other countdown badge. The countdown math itself uses
  // Date.now() — using presence.now would lag the display by up to one tick
  // interval, so a fresh "5 min" pick could round up to "6 min".
  void presence.now;
  const remaining = isCountingDown ? formatRemaining(status!.occupiedUntil!) : null;
  const label = t(STATUS_LABEL_KEYS[state]);
  const title = remaining ? `${label} • ${remaining}` : label;

  return (
    <span className={classnames(styles.Badge, className)} title={title}>
      <span
        className={classnames(styles.Dot, isOffline && styles.DotOffline)}
        style={isOffline ? undefined : { background: color }}
        aria-hidden="true"
      />
      {!iconOnly && (
        <>
          <span className={isOffline ? styles.LabelMuted : styles.Label}>{label}</span>
          {remaining && <span className={styles.Duration}>• {remaining}</span>}
        </>
      )}
    </span>
  );
});

export default StatusBadge;
